import React from 'react';
import { motion } from 'framer-motion';
import { Building2, User, Calendar, CircleDollarSign, MapPin } from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { COMPANY_INFO } from '@/lib/index';
import { useLanguage } from '@/hooks/useLanguage';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

/**
 * Company Overview Page
 * Provides detailed corporate information for M.M.K Co., Ltd.
 * Designed for institutional trust, bank audits, and immigration references.
 */
const Company: React.FC = () => {
  const { t, isEn } = useLanguage();

  const pageTitle = t({
    jp: '会社概要',
    en: 'Company Profile'
  });

  const details = [
    {
      icon: <Building2 className="w-5 h-5 text-primary" />,
      label: t({ jp: '社名', en: 'Company Name' }),
      value: t(COMPANY_INFO.name),
    },
    {
      icon: <User className="w-5 h-5 text-primary" />,
      label: t({ jp: '代表取締役', en: 'Representative Director' }),
      value: t(COMPANY_INFO.representative),
    },
    {
      icon: <Calendar className="w-5 h-5 text-primary" />,
      label: t({ jp: '設立', en: 'Established' }),
      value: t(COMPANY_INFO.established),
      isMono: true,
    },
    {
      icon: <CircleDollarSign className="w-5 h-5 text-primary" />,
      label: t({ jp: '資本金', en: 'Capital' }),
      value: t(COMPANY_INFO.capital),
      isMono: true,
    },
    {
      icon: <MapPin className="w-5 h-5 text-primary" />,
      label: t({ jp: '所在地', en: 'Location' }),
      value: t(COMPANY_INFO.location),
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.OFFICE_BUILDING_2} 
            alt="M.M.K Office Building"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            {pageTitle}
          </h1>
          <div className="mt-4 w-12 h-1 bg-primary mx-auto" />
        </motion.div>
      </section>

      {/* Corporate Information Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="border-none shadow-none bg-card/50 backdrop-blur-sm">
              <CardContent className="p-0">
                <div className="space-y-0">
                  {details.map((item, index) => (
                    <div key={index}>
                      <div className="grid grid-cols-1 md:grid-cols-3 py-8 px-6 md:px-10 items-center hover:bg-muted/30 transition-colors">
                        <div className="flex items-center gap-3 mb-2 md:mb-0">
                          {item.icon}
                          <span className="font-semibold text-foreground/80">
                            {item.label}
                          </span>
                        </div>
                        <div className="md:col-span-2">
                          <p className={`text-lg text-foreground ${item.isMono ? 'font-mono' : 'font-sans'}`}>
                            {item.value}
                          </p>
                        </div>
                      </div>
                      {index < details.length - 1 && <Separator className="opacity-50" />}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Additional Institutional Note */}
            <div className="mt-12 p-6 bg-muted/20 border border-border/40 rounded-lg">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {isEn 
                  ? 'M.M.K Co., Ltd. operates with a long-term vision focused on multicultural synergy and business sustainability within the Japanese market. We adhere to all local corporate regulations and maintain transparent management practices.'
                  : '株式会社M.M.Kは、日本国内の法令を遵守し、透明性の高い経営体制を維持しています。多文化理解と持続可能な事業運営を通じて、日本市場における長期的な価値創出を目指しております。'}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer-like bottom spacing */}
      <div className="h-12 bg-background" />
    </div>
  );
};

export default Company;